import { useMemo } from 'react';
import { useTranslation } from 'src/hooks/use-translation';

import { PATHS } from 'src/routes/paths';
import { usePermissions, RESOURCES } from 'src/hooks/use-permissions';
import { useTariff, TARIFF_FEATURES } from 'src/hooks/use-tariff';
import { useLiveSessionsEnabled } from 'src/hooks/use-live-sessions-enabled';
import { TariffLockIcon } from 'src/components/tariff';
import type { ResourcePathAllowing } from 'src/utils/permissions';
import type { TariffFeature } from 'src/types';
import { DEVELOPMENT } from 'src/config-global';

import type { NavItemBaseProps } from './sidebar/types';

import { ChatsNavBadge } from './chats-nav-badge';

import CourseSvg from 'icons/monochrome/course.svg';
import CollectionSvg from 'icons/monochrome/move-folder.svg';
import StudentSvg from 'icons/monochrome/student.svg';
import BuildingsSvg from 'icons/monochrome/buildings-bold.svg';
import ClipboardListSvg from 'icons/monochrome/clipboard-list-bold.svg';
import SettingsSvg from 'icons/monochrome/settings.svg';
import MembersSvg from 'icons/monochrome/members.svg';
import MenuSvg from 'icons/monochrome/menu.svg';
import AnalyticsSvg from 'icons/monochrome/analytics.svg';
import SubmissionSvg from 'icons/monochrome/submission.svg';
import FeedbackSvg from 'icons/monochrome/feedback.svg';
import ExamSvg from 'icons/monochrome/exam-submission.svg';
import ClockCircleTwotoneSvg from 'icons/monochrome/clock-circle-twotone.svg';
import WalletSvg from 'icons/monochrome/wallet.svg';
import BillListSvg from 'icons/monochrome/bill-list.svg';
import ChatbotSvg from 'icons/monochrome/chatbot.svg';
import ChatRoundSvg from 'icons/monochrome/chat-round.svg';
import VideoSvg from 'icons/monochrome/video-bold.svg';
import WidgetAddSvg from 'icons/monochrome/widget-add.svg';

// ----------------------------------------------------------------------

const ICONS = {
  courses: <CourseSvg />,
  courseCollections: <CollectionSvg />,
  students: <StudentSvg />,
  orgStructure: <BuildingsSvg />,
  assignments: <ClipboardListSvg />,
  categories: <MenuSvg />,
  submissions: <SubmissionSvg />,
  members: <MembersSvg />,
  settings: <SettingsSvg />,
  analytics: <AnalyticsSvg />,
  feedbacks: <FeedbackSvg />,
  examSubmissions: <ExamSvg />,
  auditLogs: <ClockCircleTwotoneSvg />,
  paymentMethods: <WalletSvg />,
  salesHistory: <BillListSvg />,
  chatbot: <ChatbotSvg />,
  chats: <ChatRoundSvg />,
  liveSessions: <VideoSvg />,
  integrations: <WidgetAddSvg />,
};

// ----------------------------------------------------------------------

// Adds a lock badge to every item (at any depth) whose `tariffFeature` is not
function applyTariffLocks(
  items: NavItemBaseProps[],
  canUseFeature: (feature: TariffFeature) => boolean
): NavItemBaseProps[] {
  return items.map((item) => ({
    ...item,
    ...(item.tariffFeature && !canUseFeature(item.tariffFeature)
      ? { info: <TariffLockIcon /> }
      : {}),
    ...(item.children ? { children: applyTariffLocks(item.children, canUseFeature) } : {}),
  }));
}

const keepPermitted =
  (canGet: (resource: ResourcePathAllowing<'get'>) => boolean) =>
  (item: NavItemBaseProps): NavItemBaseProps[] => {
    if (item.resource && !canGet(item.resource as ResourcePathAllowing<'get'>)) {
      return [];
    }

    if (!item.children) {
      return [item];
    }

    const children = item.children.flatMap(keepPermitted(canGet));

    return children.length ? [{ ...item, children }] : [];
  };

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useTranslation('', { keyPrefix: 'sidebar' });
  const { canGet } = usePermissions();
  const { canUseFeature } = useTariff();
  // TODO: Enable below feature flags when the features are ready to be used in production
  // const courseCollectionsEnabled = useFeatureFlagEnabled('course-collections');
  const liveSessionsEnabled = useLiveSessionsEnabled();
  // const salesHistoryEnabled = useFeatureFlagEnabled('sales-history');
  // const paymentMethodsEnabled = useFeatureFlagEnabled('payment-methods');

  const data = useMemo(
    () =>
      [
        {
          subheader: t('management'),
          items: applyTariffLocks(
            [
              {
                title: t('courses'),
                path: PATHS.app.courses,
                icon: ICONS.courses,
                resource: RESOURCES.COURSE.MAIN,
              },
              ...(liveSessionsEnabled
                ? [
                    {
                      title: t('live-sessions'),
                      path: PATHS.app.liveSessions,
                      icon: ICONS.liveSessions,
                      resource: RESOURCES.LIVE_SESSION.MAIN,
                    },
                  ]
                : []),
              ...(DEVELOPMENT
                ? [
                    {
                      title: t('course-collections'),
                      path: PATHS.app.courseCollections,
                      icon: ICONS.courseCollections,
                      resource: RESOURCES.COURSE_COLLECTION.MAIN,
                    },
                  ]
                : []),
              ...(DEVELOPMENT
                ? [
                    {
                      title: t('assignments'),
                      path: PATHS.app.assignments,
                      icon: ICONS.assignments,
                      resource: RESOURCES.ASSIGNMENT.MAIN,
                    },
                  ]
                : []),
              {
                title: t('students'),
                path: PATHS.app.students,
                icon: ICONS.students,
                resource: RESOURCES.STUDENT.MAIN,
              },
              ...(DEVELOPMENT
                ? [
                    {
                      title: t('org-structure'),
                      path: PATHS.app.orgStructure,
                      icon: ICONS.orgStructure,
                      resource: RESOURCES.ORG_STRUCTURE.UNITS,
                    },
                  ]
                : []),
              // {
              //   title: t('categories'),
              //   path: PATHS.app.categories,
              //   icon: ICONS.categories,
              //   resource: RESOURCE_TYPES.CATEGORIES,
              // },
              {
                title: t('submissions'),
                path: PATHS.app.submissions,
                icon: ICONS.submissions,
                resource: RESOURCES.SUBMISSION.MAIN,
              },
              {
                title: t('feedbacks'),
                path: PATHS.app.feedbacks,
                icon: ICONS.feedbacks,
                resource: RESOURCES.FEEDBACK.MAIN,
              },
              {
                title: t('exam-submissions'),
                path: PATHS.app.examSubmissions,
                icon: ICONS.examSubmissions,
                resource: RESOURCES.EXAM_SUBMISSION.MAIN,
              },
              {
                title: t('members'),
                path: PATHS.app.members,
                icon: ICONS.members,
                resource: RESOURCES.MEMBER.MAIN,
              },
              {
                title: t('analytics'),
                path: PATHS.app.analytics,
                icon: ICONS.analytics,
                resource: RESOURCES.ANALYTICS.MAIN,
                children: [
                  ...(DEVELOPMENT
                    ? [
                        {
                          title: t('training-control'),
                          path: PATHS.app.analyticsTrainingControl,
                          resource: RESOURCES.ANALYTICS.TRAINING_CONTROL,
                        },
                      ]
                    : []),
                  {
                    title: t('progress'),
                    path: PATHS.app.analyticsProgress,
                    resource: RESOURCES.ANALYTICS.PROGRESS,
                    children: [
                      {
                        title: t('progress-by-students'),
                        path: PATHS.app.analyticsProgress,
                        resource: RESOURCES.ANALYTICS.PROGRESS,
                      },
                      {
                        title: t('progress-by-members'),
                        path: PATHS.app.analyticsProgressByMembers,
                        resource: RESOURCES.ANALYTICS.MEMBERS,
                        tariffFeature: TARIFF_FEATURES.MEMBER_ANALYTICS,
                      },
                      {
                        title: t('progress-by-content'),
                        path: PATHS.app.analyticsProgressByContent,
                        resource: RESOURCES.ANALYTICS.CONTENT,
                      },
                    ],
                  },
                  {
                    title: t('points'),
                    path: PATHS.app.analyticsPoints,
                    resource: RESOURCES.ANALYTICS.POINTS,
                  },
                ],
              },
              ...(DEVELOPMENT
                ? [
                    {
                      title: t('sales-history'),
                      path: PATHS.app.salesHistory,
                      icon: ICONS.salesHistory,
                      resource: RESOURCES.SALES_HISTORY.MAIN,
                    },
                  ]
                : []),
              ...(DEVELOPMENT
                ? [
                    {
                      title: t('chatbot'),
                      path: PATHS.app.chatbot,
                      icon: ICONS.chatbot,
                      resource: RESOURCES.CHATBOT.MAIN,
                    },
                  ]
                : []),
              {
                title: t('chats'),
                path: PATHS.app.chats,
                icon: ICONS.chats,
                resource: RESOURCES.CHAT.MAIN,
                info: <ChatsNavBadge />,
              },
            ].flatMap(keepPermitted(canGet)),
            canUseFeature
          ),
        },

        {
          subheader: t('others'),
          items: applyTariffLocks(
            [
              // {
              //   title: t('feedback'),
              //   path: PATHS.feedback,
              //   icon: ICONS.info,
              //   disabled: true,
              // },
              {
                title: t('settings'),
                path: PATHS.app.settings,
                icon: ICONS.settings,
                // disabled: true,
                children: [
                  {
                    title: t('profile-settings'),
                    path: PATHS.app.profileSettings,
                    resource: RESOURCES.PROFILE_SETTINGS.MAIN,
                  },
                  {
                    title: t('platform-settings'),
                    path: PATHS.app.platformSettings,
                    resource: RESOURCES.PLATFORM_SETTINGS.MAIN,
                  },
                  {
                    title: t('platform-billing'),
                    path: PATHS.app.platformBilling,
                    resource: RESOURCES.SUBSCRIPTION.MAIN,
                  },
                ],
              },
              {
                title: t('audit-logs'),
                path: PATHS.app.auditLogs,
                icon: ICONS.auditLogs,
                resource: RESOURCES.AUDIT_LOG.MAIN,
              },
              {
                title: t('integrations'),
                path: PATHS.app.integrations,
                icon: ICONS.integrations,
                resource: RESOURCES.INTEGRATION.MAIN,
              },
              ...(DEVELOPMENT
                ? [
                    {
                      title: t('payment-methods'),
                      path: PATHS.app.paymentMethods,
                      icon: ICONS.paymentMethods,
                      resource: RESOURCES.PAYMENT.MAIN,
                    },
                  ]
                : []),
            ].flatMap(keepPermitted(canGet)),
            canUseFeature
          ),
        },
      ].filter((group) => group.items.length > 0),
    [
      t,
      // courseCollectionsEnabled,
      // salesHistoryEnabled,
      // paymentMethodsEnabled,
      canGet,
      canUseFeature,
      liveSessionsEnabled,
    ]
  );

  return data;
}
